import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import "./EventCard.css";
import "./Navbar";
import "./Footer";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faMapMarkerAlt, 
  faUserTie,
  faStore,
  faTicketAlt,
  faShoppingCart,
  faArrowRight,
  faChartPie,
  faUsers,
  faChair,
  faInfoCircle,
  faStar,
  faCheckCircle,
  faTimesCircle,
  faLocationDot,
  faCalendarDay,
  faBuilding
} from '@fortawesome/free-solid-svg-icons';
import MapTailerView from './MapTailerView';
import Navbar from "./Navbar";
import Footer from "./Footer";

// API Base URL
const API_BASE_URL = 'https://royaleheritagefiesta.com/royaleheritagefiesta/api';

function EventCard() {
  const location = useLocation();
  const navigate = useNavigate();

  const eventId = location.state?.eventId;
  const [event, setEvent] = useState(null);
  const [ticketStats, setTicketStats] = useState({
    total: 0,
    booked: 0,
    available: 0
  });
  const [stallStats, setStallStats] = useState({
    total: 0,
    booked: 0,
    available: 0
  });
  const [showMap, setShowMap] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showFloatingButtons, setShowFloatingButtons] = useState(false);

  console.log("Received eventId:", eventId);

  // Scroll listener for floating buttons
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
      setShowFloatingButtons(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/9966252374?text=Hi%20Royal%20Heritage%20Festia%20team,%20I%27m%20interested%20in%20this%20event.', '_blank');
  };

  // Helper function to get total stalls by pattern
  const getTotalStallsByPattern = (pattern) => {
    switch(pattern) {
      case "full":
        return 50;
      case "ushape":
        return 50;
      case "twosides":
        return 50;
      default:
        return 50;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!eventId) return;
      
      setLoading(true);
      
      try {
        // ========== FETCH EVENT FROM PHP API ==========
        const eventRes = await axios.get(`${API_BASE_URL}/events.php?eventId=${eventId}`);
        const eventData = eventRes.data;
        
        if (!eventData) {
          console.error("Event not found");
          setLoading(false);
          return;
        }
        
        console.log("Event details from PHP API:", eventData);
        setEvent(eventData);
        
        // ===== FETCH STALL STATS FROM PHP API =====
        let totalStalls = 0;
        let bookedStalls = 0;
        
        if (eventData.isCustomLayout) {
          // For custom layout events - read from JSON
          console.log("Custom layout event - reading stalls from JSON");
          let layoutData = null;
          
          if (eventData.customLayoutData) {
            try {
              layoutData = typeof eventData.customLayoutData === 'string' 
                ? JSON.parse(eventData.customLayoutData) 
                : eventData.customLayoutData;
            } catch (e) {
              console.error("Error parsing layout data:", e);
            }
          }
          
          if (layoutData && layoutData.stalls) {
            const stalls = layoutData.stalls;
            totalStalls = stalls.length;
            
            // Get booked stalls from PHP API
            try {
              const bookingsRes = await axios.get(`${API_BASE_URL}/stall-bookings.php?eventId=${eventId}`);
              console.log("Bookings from API:", bookingsRes.data);
              
              let bookedStallNumbers = new Set();
              if (bookingsRes.data.success && bookingsRes.data.bookings) {
                bookingsRes.data.bookings.forEach(booking => {
                  bookedStallNumbers.add(booking.stallNumber);
                });
              } else if (Array.isArray(bookingsRes.data)) {
                bookingsRes.data.forEach(booking => {
                  bookedStallNumbers.add(booking.stallNumber);
                });
              }
              
              bookedStalls = bookedStallNumbers.size;
            } catch (bookingError) {
              console.error("Error fetching bookings:", bookingError);
              bookedStalls = 0;
            }
            
            console.log(`Custom layout - Total stalls: ${totalStalls}, Booked: ${bookedStalls}`);
          }
        } else {
          // For regular events - generate stalls based on pattern
          totalStalls = getTotalStallsByPattern(eventData.stallPattern);
          
          // Get booked stalls from PHP API
          try {
            const bookingsRes = await axios.get(`${API_BASE_URL}/stall-bookings.php?eventId=${eventId}`);
            console.log("Bookings from API:", bookingsRes.data);
            
            if (bookingsRes.data.success && bookingsRes.data.bookings) {
              bookedStalls = bookingsRes.data.bookings.length;
            } else if (Array.isArray(bookingsRes.data)) {
              bookedStalls = bookingsRes.data.length;
            } else {
              bookedStalls = 0;
            }
          } catch (bookingError) {
            console.error("Error fetching bookings:", bookingError);
            bookedStalls = 0;
          }
        }
        
        setStallStats({
          total: totalStalls,
          booked: bookedStalls,
          available: totalStalls - bookedStalls
        });
        
        // ===== FETCH TICKET STATS FROM PHP API =====
        let bookedTickets = 0;
        
        try {
          const ticketRes = await axios.get(`${API_BASE_URL}/ticket-bookings.php?eventId=${eventId}`);
          console.log("Ticket bookings from API:", ticketRes.data);
          
          if (Array.isArray(ticketRes.data)) {
            if (!eventData.hasSeating) {
              // Standing event - count quantities
              bookedTickets = ticketRes.data.reduce((sum, booking) => sum + (booking.quantity || 1), 0);
            } else {
              // Seated event - count individual bookings
              bookedTickets = ticketRes.data.length;
            }
          }
        } catch (ticketError) {
          console.error("Error fetching ticket bookings:", ticketError);
          bookedTickets = 0;
        }
        
        let totalTickets = 0;
        if (eventData.hasSeating) {
          totalTickets = eventData.totalSeats || 0;
        } else {
          totalTickets = eventData.standingCapacity || 0;
        }
        
        setTicketStats({
          total: totalTickets,
          booked: bookedTickets,
          available: totalTickets - bookedTickets
        });
        
        setLoading(false);
        
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [eventId]);

  if (!eventId) return (
    <div className="event-error-container">
      <div className="event-error-card">
        <span className="event-error-icon">❌</span>
        <h3>No Event Selected</h3>
        <p>Please select an event from the calendar to view details</p>
        <button className="event-back-btn" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    </div>
  );
  
  if (loading) return (
    <div className="event-loading-container">
      <div className="event-loader"></div>
      <p>Loading event details...</p>
    </div>
  );

  if (!event) return (
    <div className="event-error-container">
      <div className="event-error-card">
        <span className="event-error-icon">⚠️</span>
        <h3>Event Not Found</h3>
        <p>The requested event could not be found</p>
        <button className="event-back-btn" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    </div>
  );

  const totalStalls = stallStats.total;
  const bookedStalls = stallStats.booked;
  const availableStalls = stallStats.available;
  const stallOccupancyRate = totalStalls > 0 ? ((bookedStalls / totalStalls) * 100).toFixed(1) : "0.0";
  
  const totalCapacity = event.hasSeating 
    ? (event.totalSeats || 0) 
    : (event.standingCapacity || 0);
  
  const bookedTickets = ticketStats.booked;
  const availableTickets = totalCapacity > 0 ? totalCapacity - bookedTickets : '∞';
  const ticketOccupancyRate = totalCapacity > 0 
    ? ((bookedTickets / totalCapacity) * 100).toFixed(1) 
    : "0.0";

  const venue = event.place || 'Venue to be announced';
  const eventDate = event.eventDate ? new Date(event.eventDate).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : 'Date TBA';

  const formatHighlights = (highlightsText) => {
    if (!highlightsText) return null;
    
    if (highlightsText.includes('•') || highlightsText.includes('\n')) {
      const lines = highlightsText.split('\n').filter(line => line.trim() !== '');
      
      return (
        <ul className="highlights-list">
          {lines.map((line, index) => {
            const cleanLine = line.replace(/^[•\-*]\s*/, '').trim();
            if (cleanLine) {
              return <li key={index}>• {cleanLine}</li>;
            }
            return null;
          })}
        </ul>
      );
    } else {
      const sentences = highlightsText.split(/[.!?]+/).filter(s => s.trim().length > 0);
      
      if (sentences.length > 1) {
        return (
          <ul className="highlights-list">
            {sentences.map((sentence, index) => (
              <li key={index}>• {sentence.trim()}</li>
            ))}
          </ul>
        );
      } else {
        const keywords = ['SPECIAL PERFORMER', 'TIMINGS', 'STALLS', 'PARKING', 'RESPONSIBLE'];
        let formattedText = highlightsText;
        
        keywords.forEach(keyword => {
          const regex = new RegExp(`(${keyword}[^.]*)`, 'gi');
          formattedText = formattedText.replace(regex, '\n• $1');
        });
        
        const lines = formattedText.split('\n').filter(line => line.trim() !== '');
        
        return (
          <ul className="highlights-list">
            {lines.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        );
      }
    }
  };

  return (
    <> 
      <Navbar />

      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body { 
          background: #111; 
          font-family: 'Montserrat', sans-serif; 
          margin: 0; 
          color: #333; 
        }

        .event-detail-wrapper {
          background: #f5f5f5;
          margin: 0 auto;
          width: 98%;
          max-width: 1400px;
          border-left: 17px solid #000;
          border-right: 17px solid #000;
          border-radius: 0;
          padding: 2rem 1rem;
          min-height: calc(100vh - 200px);
        }

        .event-detail-container {
          max-width: 1000px;
          margin: 0 auto;
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          border: 1px solid #eee;
        }

        /* Header Section */
        .event-header {
          background: linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%);
          color: white;
          padding: 2.5rem;
          position: relative;
        }

        .event-badge {
          display: inline-block;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          padding: 0.3rem 1rem;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 1px;
          margin-bottom: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #fff;
        }

        .event-title {
          font-family: 'Cinzel', serif;
          font-size: 2rem;
          font-weight: 700;
          margin: 0 0 1.5rem 0;
          line-height: 1.2;
          color: #fff;
        }

        .event-highlights-section {
          background: rgba(255, 255, 255, 0.08);
          border-left: 4px solid #fff;
          border-radius: 8px;
          padding: 1rem;
          margin: 1rem 0 1.5rem 0;
        }

        .event-highlights-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 0.8rem;
        }

        .event-highlights-icon {
          color: #fff;
          font-size: 1rem;
        }

        .event-highlights-title {
          font-weight: 700;
          font-size: 0.85rem;
          color: #fff;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin: 0;
        }

        .highlights-list {
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .highlights-list li {
          margin-bottom: 0.5rem;
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.85rem;
          line-height: 1.5;
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }

        /* Event Meta - White text */
        .event-meta {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }

        .event-meta-item {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          padding: 0.8rem 1rem;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .event-meta-item:hover {
          background: rgba(255, 255, 255, 0.15);
          transform: translateY(-2px);
        }

        .event-meta-icon {
          font-size: 1.2rem;
          color: #fff;
        }

        .event-meta-text {
          display: flex;
          flex-direction: column;
        }

        .event-meta-label {
          font-size: 0.6rem;
          opacity: 0.7;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 2px;
          color: white !important;
        }

        .event-meta-value {
          font-weight: 600;
          font-size: 0.85rem;
          color: white !important;
        }

        /* Navigation Tabs and Action Buttons Container */
        .event-nav-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .event-tabs {
          display: flex;
          gap: 0.5rem;
        }

        .event-tab {
          padding: 0.6rem 1.2rem;
          border: none;
          background: transparent;
          color: #6c757d;
          font-weight: 600;
          font-size: 0.85rem;
          border-radius: 30px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .event-tab:hover {
          background: #000;
          color: white;
        }

        .event-tab.active {
          background: #000;
          color: white;
        }

        /* Quick Action Buttons in Navigation */
        .nav-action-buttons {
          display: flex;
          gap: 0.8rem;
        }

        .nav-action-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0.6rem 1.2rem;
          border: 2px solid #000;
          border-radius: 30px;
          cursor: pointer;
          transition: all 0.3s ease;
          background: #fff;
          font-weight: 600;
          font-size: 0.8rem;
          color: #000;
        }

        .nav-action-btn:hover {
          background: #000;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .nav-action-btn:hover .nav-action-icon {
          color: white;
        }

        .nav-action-icon {
          font-size: 1rem;
          color: #000;
        }

        /* Overview Tab */
        .event-overview {
          padding: 1.5rem;
        }

        .event-description-section {
          margin-bottom: 2rem;
        }

        .event-description-section h3 {
          font-family: 'Cinzel', serif;
          font-size: 1.2rem;
          font-weight: 600;
          color: #1e2b3a;
          margin: 0 0 1rem 0;
        }

        .event-description-text {
          color: #495057;
          line-height: 1.6;
          font-size: 0.9rem;
        }

        /* Stats Grid */
        .event-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .event-stat-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          transition: all 0.3s ease;
          border: 1px solid #e9ecef;
        }

        .event-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
          border-color: #000;
        }

        .event-stat-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          background: #000;
          color: white;
        }

        .event-stat-content {
          display: flex;
          flex-direction: column;
        }

        .event-stat-label {
          font-size: 0.7rem;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .event-stat-value {
          font-size: 1.3rem;
          font-weight: 700;
          color: #1e2b3a;
        }

        /* Stalls & Tickets Tab */
        .event-stalls-tab,
        .event-tickets-tab {
          padding: 1.5rem;
        }

        .event-stalls-stats,
        .event-tickets-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .event-stalls-stat,
        .event-tickets-stat {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 1rem;
          text-align: center;
          border: 1px solid #e9ecef;
        }

        .event-stalls-stat-label,
        .event-tickets-stat-label {
          display: block;
          font-size: 0.7rem;
          color: #6c757d;
          margin-bottom: 0.3rem;
          text-transform: uppercase;
        }

        .event-stalls-stat-value,
        .event-tickets-stat-value {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e2b3a;
        }

        .event-stalls-stat-value.booked,
        .event-tickets-stat-value.booked {
          color: #000;
        }

        .event-stalls-stat-value.available,
        .event-tickets-stat-value.available {
          color: #28a745;
        }

        /* Progress Bar */
        .event-progress-section {
          margin-bottom: 1.5rem;
        }

        .event-progress-label {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5rem;
          font-size: 0.8rem;
          color: #495057;
        }

        .event-progress-percentage {
          color: #000;
          font-weight: 600;
        }

        .event-progress-bar {
          height: 8px;
          background: #e9ecef;
          border-radius: 4px;
          overflow: hidden;
        }

        .event-progress-fill {
          height: 100%;
          background: #000;
          border-radius: 4px;
          transition: width 0.5s ease;
        }

        /* Legend */
        .event-legend {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          margin: 1.5rem 0;
        }

        .event-legend-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8rem;
          color: #495057;
        }

        .event-legend-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }

        .event-legend-dot.available {
          background: #28a745;
        }

        .event-legend-dot.booked {
          background: #000;
        }

        /* Seating Info */
        .event-seating-info {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 1rem;
          margin: 1.5rem 0;
          display: flex;
          align-items: center;
          gap: 1rem;
          border: 1px solid #e9ecef;
        }

        .event-seating-icon {
          font-size: 1.5rem;
          color: #000;
        }

        .event-seating-text {
          flex: 1;
        }

        .event-seating-label {
          display: block;
          font-size: 0.7rem;
          color: #6c757d;
          text-transform: uppercase;
          margin-bottom: 2px;
        }

        .event-seating-value {
          display: block;
          font-size: 0.9rem;
          font-weight: 600;
          color: #1e2b3a;
        }

        .event-seating-type {
          font-size: 0.75rem;
          font-weight: 400;
          color: #6c757d;
          margin-left: 0.5rem;
        }

        .full-width {
          margin-top: 1rem;
          width: 100%;
        }

        /* Footer */
        .event-footer {
          padding: 1rem 1.5rem;
          background: #f8f9fa;
          border-top: 1px solid #e9ecef;
          text-align: center;
        }

        .event-footer-text {
          color: #6c757d;
          font-size: 0.8rem;
          margin: 0;
        }

        /* Error & Loading States */
        .event-error-container,
        .event-loading-container {
          min-height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          background: #f5f5f5;
          margin: 0 auto;
          width: 98%;
          max-width: 1400px;
          border-left: 17px solid #000;
          border-right: 17px solid #000;
          padding: 2rem;
        }

        .event-error-card {
          background: white;
          padding: 2rem;
          border-radius: 20px;
          text-align: center;
          max-width: 400px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          border: 1px solid #eee;
        }

        .event-error-icon {
          font-size: 3rem;
          display: block;
          margin-bottom: 1rem;
        }

        .event-error-card h3 {
          color: #1e2b3a;
          margin-bottom: 0.5rem;
          font-family: 'Cinzel', serif;
        }

        .event-error-card p {
          color: #6c757d;
          margin-bottom: 1.5rem;
        }

        .event-back-btn {
          padding: 0.6rem 1.5rem;
          background: #000;
          color: white;
          border: none;
          border-radius: 30px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .event-back-btn:hover {
          background: #333;
          transform: translateY(-2px);
        }

        .event-loader {
          width: 40px;
          height: 40px;
          border: 3px solid #e9ecef;
          border-top-color: #000;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        /* Floating Buttons */
        .floating-buttons {
          position: fixed;
          bottom: 20px;
          right: 20px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          z-index: 1000;
          opacity: 0;
          visibility: hidden;
          transform: translateY(20px);
          transition: 0.3s;
        }

        .floating-buttons.show {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .floating-btn {
          width: 45px;
          height: 45px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          transition: 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          color: #fff;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
        }

        .floating-btn.whatsapp {
          background: #25d366;
        }

        .floating-btn.top {
          background: #000;
        }

        .floating-btn:hover {
          transform: scale(1.1);
        }

        .back-to-top {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 45px;
          height: 45px;
          background: #000;
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          visibility: hidden;
          transition: 0.2s;
          z-index: 99;
          border: none;
        }

        .back-to-top.show {
          opacity: 1;
          visibility: visible;
        }

        .back-to-top:hover {
          background: #333;
          transform: translateY(-3px);
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 992px) {
          .event-detail-wrapper {
            width: 96%;
            padding: 1rem;
          }
          
          .event-title {
            font-size: 1.6rem;
          }
          
          .event-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .event-header {
            padding: 1.5rem;
          }
          
          .event-detail-wrapper {
            width: 100% !important;
            border-left: 17.5px solid #000 !important;
            border-right: 17.5px solid #000 !important;
            padding: 1rem !important;
          }
          
          .event-error-container,
          .event-loading-container {
            width: 100% !important;
            border-left: 17px solid #000 !important;
            border-right: 17px solid #000 !important;
          }
          
          .event-title {
            font-size: 1.4rem;
          }
          
          .event-meta {
            grid-template-columns: 1fr;
          }
          
          .event-nav-container {
            flex-direction: column;
            align-items: stretch;
            padding: 1rem;
          }
          
          .event-tabs {
            justify-content: center;
            overflow-x: auto;
          }
          
          .event-tab {
            padding: 0.4rem 1rem;
            white-space: nowrap;
            font-size: 0.75rem;
          }
          
          .nav-action-buttons {
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .nav-action-btn {
            justify-content: center;
            padding: 0.5rem 1rem;
          }
          
          .event-overview,
          .event-stalls-tab,
          .event-tickets-tab {
            padding: 1rem;
          }
          
          .event-stats-grid {
            grid-template-columns: 1fr;
          }
          
          .event-stalls-stats,
          .event-tickets-stats {
            grid-template-columns: 1fr;
          }
          
          .event-legend {
            flex-direction: column;
            align-items: center;
            gap: 0.5rem;
          }
          
          .event-seating-info {
            flex-direction: column;
            text-align: center;
            padding: 0.8rem;
          }
        }

        @media (max-width: 576px) {
          .event-detail-wrapper {
            width: 100%;
            padding: 0.5rem;
          }
          
          .event-title {
            font-size: 1.2rem;
          }
          
          .event-badge {
            font-size: 0.6rem;
          }
          
          .highlights-list li {
            font-size: 0.75rem;
          }
          
          .nav-action-btn {
            font-size: 0.7rem;
          }
        }
      `}</style>

      <div className="event-detail-wrapper">
        <div className="event-detail-container">
          
          {/* Header Section */}
          <div className="event-header">
            <div className="event-header-content">
              <span className="event-badge">LIVE EVENT</span>
              <h1 className="event-title">{event.eventName}</h1>
              
              {event.highlights && (
                <div className="event-highlights-section">
                  <div className="event-highlights-header">
                    <FontAwesomeIcon icon={faStar} className="event-highlights-icon" />
                    <h3 className="event-highlights-title">EVENT HIGHLIGHTS</h3>
                  </div>
                  <div className="event-highlights-content">
                    {formatHighlights(event.highlights)}
                  </div>
                </div>
              )}
              
              <div className="event-meta">
                <div className="event-meta-item" onClick={() => setShowMap(true)}>
                  <FontAwesomeIcon icon={faLocationDot} className="event-meta-icon" />
                  <div className="event-meta-text">
                    <span className="event-meta-label">VENUE</span>
                    <span className="event-meta-value">{venue}</span>
                  </div>
                </div>
                <div className="event-meta-item">
                  <FontAwesomeIcon icon={faCalendarDay} className="event-meta-icon" />
                  <div className="event-meta-text">
                    <span className="event-meta-label">DATE</span>
                    <span className="event-meta-value">{eventDate}</span>
                  </div>
                </div>
                <div className="event-meta-item">
                  <FontAwesomeIcon icon={faBuilding} className="event-meta-icon" />
                  <div className="event-meta-text">
                    <span className="event-meta-label">SPONSOR</span>
                    <span className="event-meta-value">{event.spansour || 'To be announced'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs + Quick Action Buttons */}
          <div className="event-nav-container">
            <div className="event-tabs">
              <button 
                className={`event-tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
                Overview
              </button>
              <button 
                className={`event-tab ${activeTab === 'stalls' ? 'active' : ''}`}
                onClick={() => setActiveTab('stalls')}
              >
                <FontAwesomeIcon icon={faStore} />
                Stalls
              </button>
              <button 
                className={`event-tab ${activeTab === 'tickets' ? 'active' : ''}`}
                onClick={() => setActiveTab('tickets')}
              >
                <FontAwesomeIcon icon={faTicketAlt} />
                Tickets
              </button>
            </div>

            <div className="nav-action-buttons">
              <button
                className="nav-action-btn"
                onClick={() => navigate("/Booking_event", { state: { eventId, eventData: event } })}
              >
                <FontAwesomeIcon icon={faStore} className="nav-action-icon" />
                Book Stalls
              </button>
              <button
                className="nav-action-btn"
                onClick={() => navigate("/BookingTicket", { state: { eventId, eventData: event } })}
              >
                <FontAwesomeIcon icon={faTicketAlt} className="nav-action-icon" />
                Buy Tickets
              </button>
              <button
                className="nav-action-btn"
                onClick={() => setShowMap(true)}
              >
                <FontAwesomeIcon icon={faLocationDot} className="nav-action-icon" />
                View Location
              </button>
            </div>
          </div>

          {/* Overview Tab Content */}
          {activeTab === 'overview' && (
            <div className="event-overview">
              <div className="event-description-section">
                <h3>About the Event</h3>
                <div className="event-description-text">
                  <p>
                    {event.eventName} is a grand celebration bringing together culture, 
                    entertainment, and business opportunities. Join us for an unforgettable 
                    experience filled with amazing performances, delicious food, and exciting 
                    shopping opportunities.
                  </p>
                </div>
              </div>

              <div className="event-stats-grid">
                <div className="event-stat-card">
                  <div className="event-stat-icon">
                    <FontAwesomeIcon icon={faStore} />
                  </div>
                  <div className="event-stat-content">
                    <span className="event-stat-label">Total Stalls</span>
                    <span className="event-stat-value">{totalStalls}</span>
                  </div>
                </div>
                <div className="event-stat-card">
                  <div className="event-stat-icon">
                    <FontAwesomeIcon icon={faTicketAlt} />
                  </div>
                  <div className="event-stat-content">
                    <span className="event-stat-label">Total Tickets</span>
                    <span className="event-stat-value">{totalCapacity}</span>
                  </div>
                </div>
                <div className="event-stat-card">
                  <div className="event-stat-icon">
                    <FontAwesomeIcon icon={faChartPie} />
                  </div>
                  <div className="event-stat-content">
                    <span className="event-stat-label">Stall Occupancy</span>
                    <span className="event-stat-value">{stallOccupancyRate}%</span>
                  </div>
                </div>
                <div className="event-stat-card">
                  <div className="event-stat-icon">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div className="event-stat-content">
                    <span className="event-stat-label">Ticket Sales</span>
                    <span className="event-stat-value">{ticketOccupancyRate}%</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stalls Tab Content */}
          {activeTab === 'stalls' && (
            <div className="event-stalls-tab">
              <h3 className="event-section-title">Stall Availability</h3>
              
              <div className="event-stalls-stats">
                <div className="event-stalls-stat">
                  <span className="event-stalls-stat-label">Total Stalls</span>
                  <span className="event-stalls-stat-value">{totalStalls}</span>
                </div>
                <div className="event-stalls-stat">
                  <span className="event-stalls-stat-label">Booked</span>
                  <span className="event-stalls-stat-value booked">{bookedStalls}</span>
                </div>
                <div className="event-stalls-stat">
                  <span className="event-stalls-stat-label">Available</span>
                  <span className="event-stalls-stat-value available">{availableStalls}</span>
                </div>
              </div>

              <div className="event-progress-section">
                <div className="event-progress-label">
                  <span>Occupancy Rate</span>
                  <span className="event-progress-percentage">{stallOccupancyRate}%</span>
                </div>
                <div className="event-progress-bar">
                  <div 
                    className="event-progress-fill" 
                    style={{ width: `${stallOccupancyRate}%` }}
                  ></div>
                </div>
              </div>

              <div className="event-legend">
                <div className="event-legend-item">
                  <span className="event-legend-dot available"></span>
                  <span>Available ({availableStalls})</span>
                </div>
                <div className="event-legend-item">
                  <span className="event-legend-dot booked"></span>
                  <span>Booked ({bookedStalls})</span>
                </div>
              </div>
            </div>
          )}

          {/* Tickets Tab Content */}
          {activeTab === 'tickets' && (
            <div className="event-tickets-tab">
              <h3 className="event-section-title">Ticket Availability</h3>
              
              <div className="event-tickets-stats">
                <div className="event-tickets-stat">
                  <span className="event-tickets-stat-label">Total Capacity</span>
                  <span className="event-tickets-stat-value">
                    {event.hasSeating ? totalCapacity : (event.standingCapacity > 0 ? event.standingCapacity : '∞')}
                  </span>
                </div>
                <div className="event-tickets-stat">
                  <span className="event-tickets-stat-label">Sold</span>
                  <span className="event-tickets-stat-value booked">{bookedTickets}</span>
                </div>
                <div className="event-tickets-stat">
                  <span className="event-tickets-stat-label">Available</span>
                  <span className="event-tickets-stat-value available">
                    {typeof availableTickets === 'number' ? availableTickets : '∞'}
                  </span>
                </div>
              </div>

              <div className="event-progress-section">
                <div className="event-progress-label">
                  <span>Sales Progress</span>
                  <span className="event-progress-percentage">{ticketOccupancyRate}%</span>
                </div>
                <div className="event-progress-bar">
                  <div 
                    className="event-progress-fill" 
                    style={{ width: `${ticketOccupancyRate}%` }}
                  ></div>
                </div>
              </div>

              <div className="event-seating-info">
                <FontAwesomeIcon icon={faChair} className="event-seating-icon" />
                <div className="event-seating-text">
                  <span className="event-seating-label">Seating Configuration</span>
                  <span className="event-seating-value">
                    {event.hasSeating ? (
                      <>
                        {event.seatRows} Rows × {event.seatColumns} Columns
                        <span className="event-seating-type">({event.seatingType} seating)</span>
                      </>
                    ) : (
                      'General Admission (Standing)'
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="event-footer">
            <p className="event-footer-text">
              Need assistance? Contact us at <a href="mailto:royaleheritagefiesta@gmail.com" style={{color:"#000"}}>royaleheritagefiesta@gmail.com</a>
            </p>
          </div>
        </div>

        {/* Map Popup */}
        {showMap && (
          <MapTailerView 
            venue={venue}
            onClose={() => setShowMap(false)}
          />
        )}
      </div>

      <Footer />

      {/* Floating Buttons */}
      <div className={`floating-buttons ${showFloatingButtons ? 'show' : ''}`}>
        <button className="floating-btn whatsapp" onClick={handleWhatsAppClick}>
          <i className="bi bi-whatsapp"></i>
        </button>
        <button className="floating-btn top" onClick={handleBackToTop}>
          <i className="bi bi-arrow-up"></i>
        </button>
      </div>

      {/* Back to Top Button */}
      <div className={`back-to-top ${showBackToTop ? 'show' : ''}`} onClick={handleBackToTop}>
        <i className="bi bi-arrow-up"></i>
      </div>
    </> 
  );
}

export default EventCard;